import { useCvBuilder } from "@/stores/cvBuilderStore";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Briefcase, ArrowUp, ArrowDown, GripVertical } from "lucide-react";

import { CvResponsibilityField, CvField, CvSectionsEditor } from './CvDataFields';

const ExperiencesStep = () => {
    const { draft, addExperience, updateExperience, removeExperience, moveExperience } = useCvBuilder();
    const { experiences } = draft;

    const handleChange = (index, field, value) => {
        const updated = { ...experiences[index], [field]: value };
        updateExperience(index, updated);
    };

    return (
        <div className="space-y-6" data-testid="experiences-step">
            <div className="flex justify-between items-center border-b border-white/10 pb-4">
                <h3 className="text-lg font-semibold text-white">Historique Professionnel</h3>
                <Button onClick={addExperience} className="btn-primary" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une expérience
                </Button>
            </div>

            {experiences.length === 0 && (
                <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-xl bg-white/5 flex flex-col items-center justify-center">
                    <Briefcase className="h-10 w-10 text-slate-500 mb-3 opacity-50" />
                    <p className="text-white font-medium">Aucune expérience ajoutée</p>
                    <p className="text-sm text-slate-400 mt-1">Cliquez sur &quot;Ajouter une expérience&quot; pour commencer.</p>
                </div>
            )}

            <div className="space-y-4">
                {experiences.map((exp, index) => (
                    <Card key={index} data-cv-experience={index} className="glass border-white/10">
                        <CardHeader className="py-3 px-4 bg-white/5 border-b border-white/10 flex flex-row items-center justify-between space-y-0">
                            <CardTitle className="text-sm font-bold uppercase tracking-tight text-white">
                                Expérience {index + 1}
                            </CardTitle>
                            <div className="flex items-center gap-1" aria-label={`Actions de l'expérience ${index + 1}`}>
                                <GripVertical className="h-4 w-4 text-slate-500" aria-hidden="true" />
                                <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-300 disabled:opacity-30"
                                    onClick={() => moveExperience(index, index - 1)} disabled={index === 0}
                                    aria-label={`Monter l'expérience ${index + 1}`}>
                                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-300 disabled:opacity-30"
                                    onClick={() => moveExperience(index, index + 1)} disabled={index === experiences.length - 1}
                                    aria-label={`Descendre l'expérience ${index + 1}`}>
                                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                                </Button>
                                <Button variant="ghost" size="icon"
                                    className="h-10 w-10 text-slate-400 hover:text-red-400 hover:bg-red-400/10"
                                    onClick={() => removeExperience(index)} aria-label={`Supprimer l'expérience ${index + 1}`}>
                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor={`experience-${index}-company`} className="text-slate-300">Entreprise</Label>
                                    <Input data-cv-path={`experiences.${index}.company`} id={`experience-${index}-company`}
                                        placeholder="Ex: Lenvia"
                                        value={exp.company}
                                        onChange={(e) => handleChange(index, "company", e.target.value)}
                                        className="bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor={`experience-${index}-location`} className="text-slate-300">Lieu</Label>
                                    <Input data-cv-path={`experiences.${index}.location`} id={`experience-${index}-location`}
                                        placeholder="Ville, Pays"
                                        value={exp.location}
                                        onChange={(e) => handleChange(index, "location", e.target.value)}
                                        className="bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-slate-300">Poste global</Label>
                                    <Input data-cv-path={`experiences.${index}.role`} id={`experience-${index}-role`} value={exp.role || ""} placeholder="Ex: MOE .NET"
                                        onChange={(e) => handleChange(index, "role", e.target.value)}
                                        className="bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor={`experience-${index}-client`} className="text-slate-300">Client principal (facultatif)</Label>
                                    <Input data-cv-path={`experiences.${index}.client`} id={`experience-${index}-client`} value={exp.client || ""} placeholder="Uniquement si le document l'identifie"
                                        onChange={(e) => handleChange(index, "client", e.target.value)}
                                        className="bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                <div className="space-y-2">
                                    <Label htmlFor={`experience-${index}-start_date`} className="text-slate-300">Date de début</Label>
                                    <Input data-cv-path={`experiences.${index}.start_date`} id={`experience-${index}-start_date`}
                                        type="text"
                                        placeholder="2022 ou 03/2022"
                                        value={exp.start_date}
                                        onChange={(e) => handleChange(index, "start_date", e.target.value)}
                                        className="bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                                    />
                                </div>
                                <div className="space-y-2">
                                        <Label htmlFor={`experience-${index}-end_date`} className="text-slate-300">Date de fin</Label>
                                        <Input data-cv-path={`experiences.${index}.end_date`} id={`experience-${index}-end_date`}
                                        type="text"
                                        placeholder="2024 ou 01/2024"
                                            value={exp.end_date}
                                            onChange={(e) => handleChange(index, "end_date", e.target.value)}
                                            className="bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                                        />
                                    </div>
                                <div className="flex items-center space-x-2 h-10">
                                    <Checkbox
                                        id={`current-${index}`} data-cv-path={`experiences.${index}.is_current`}
                                        checked={exp.is_current}
                                        onCheckedChange={(checked) => handleChange(index, "is_current", checked)}
                                        className="border-white/20 data-[state=checked]:bg-lenvia-blue data-[state=checked]:border-lenvia-blue"
                                    />
                                    <Label htmlFor={`current-${index}`} className="cursor-pointer text-slate-300">Poste actuel</Label>
                                </div>
                            </div>

                            {(exp.missions?.length > 0 || exp.project_groups?.length > 0) && <div className="rounded-lg border border-blue-400/25 bg-blue-400/10 p-4 space-y-2">
                                <p className="text-sm text-slate-200">{exp.missions?.length || 0} projet(s), {exp.project_groups?.length || 0} équipe(s) / squad(s). Leurs contextes, responsabilités et technologies sont renseignés séparément dans Missions.</p>
                                <Button type="button" variant="outline" onClick={() => window.dispatchEvent(new CustomEvent('cvbuilder:go-to-step', { detail: {step: 3, fieldPath: `experiences.${index}.missions`, message: `Projets et équipes de ${exp.company || 'cette expérience'}`} }))}>Voir les projets et équipes</Button>
                            </div>}
                            <div className="space-y-2">
                                <Label htmlFor={`experience-${index}-summary`} className="text-slate-300">Contexte général</Label>
                                <Textarea data-cv-path={`experiences.${index}.summary`} id={`experience-${index}-summary`} value={exp.summary || ""} rows={3}
                                    onChange={(e) => handleChange(index, "summary", e.target.value)}
                                    placeholder="Contexte attesté par le CV, sans créer de projet artificiel"
                                    className="bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue" />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <CvResponsibilityField path={`experiences.${index}`} value={exp}
                                    onChange={patch => updateExperience(index, { ...exp, ...patch })} />
                                <CvField path={`experiences.${index}.technical_environment`} label="Environnement technique transversal" value={exp.technical_environment} list rows={5}
                                    onChange={value => handleChange(index, 'technical_environment', value)} />
                            </div>

                            <details open={Boolean(exp.functional_environment?.length || exp.sections?.length) || undefined} className="rounded-lg border border-white/10 p-4">
                                <summary className="cursor-pointer text-sm text-slate-300">Périmètre et rubriques de l’expérience</summary>
                                <div className="mt-4 space-y-4">
                                    <CvField path={`experiences.${index}.functional_environment`} label="Environnement fonctionnel de l’expérience" value={exp.functional_environment} list
                                        onChange={value => handleChange(index, 'functional_environment', value)} />
                                    <CvSectionsEditor path={`experiences.${index}.sections`} value={exp.sections}
                                        onChange={value => handleChange(index, 'sections', value)} />
                                </div>
                            </details>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default ExperiencesStep;
